/* Simple localStorage cache helper
 * - Safe for SSR (no window) — functions become no-ops in non-browser envs
 * - Wraps values with metadata { value, updated, ttl }
 * - Supports TTL (milliseconds). Expired entries are removed and treated as miss.
 * - Provides a small merge helper for objects.
 */
(function () {
    'use strict';

    function _isAvailable() {
        try {
            return typeof window !== 'undefined' && !!window.localStorage;
        } catch (err) {
            // if access to window.localStorage throws error, treat as unavailable
            console.debug('localStorage not available', err && err.message);
            return false;
        }
    }

    function cacheSet(key, value, ttlMs = null) {
        if (!_isAvailable()) return;
        const item = {
            ttl: ttlMs,
            updated: Date.now(),
            value: value,
        };
        try {
            window.localStorage.setItem(key, JSON.stringify(item));
        } catch {
            // ignore quota / security errors but log for debugging
            console.debug('localStorage.setItem failed');
        }
    }

    function cacheGet(key) {
        if (!_isAvailable()) return null;
        const raw = window.localStorage.getItem(key);
        if (!raw) return null;
        try {
            const parsed = JSON.parse(raw);

            // if this is our wrapped format
            if (parsed && typeof parsed === 'object' && 'updated' in parsed && 'value' in parsed) {
                if (parsed.ttl && Date.now() - parsed.updated > parsed.ttl) {
                    // expired
                    try {
                        window.localStorage.removeItem(key);
                    } catch (removeErr) {
                        console.debug('localStorage.removeItem failed', removeErr && removeErr.message);
                    }
                    return null;
                }
                return parsed.value;
            }

            // legacy / unwrapped format: return the parsed data directly
            return parsed;
        } catch (err) {
            // corrupted JSON: remove and return null
            try {
                window.localStorage.removeItem(key);
            } catch (removeErr) {
                console.debug('localStorage.removeItem failed', removeErr && removeErr.message);
            }
            return null;
        }
    }

    function cacheRemove(key) {
        if (!_isAvailable()) return;
        try {
            window.localStorage.removeItem(key);
        } catch (err) {
            console.debug('localStorage.removeItem failed', err && err.message);
        }
    }

    if (typeof module !== 'undefined' && module.exports) {
        module.exports = {
            cacheGet,
            cacheRemove,
            cacheSet,
        };
    }

    if (typeof window !== 'undefined') {
        window.localStorageCache = window.localStorageCache || {};
        window.localStorageCache.cacheSet = cacheSet;
        window.localStorageCache.cacheGet = cacheGet;
        window.localStorageCache.cacheRemove = cacheRemove;
    }

    // Note: Exports are provided via CommonJS (module.exports) and a global
    // `window.localStorageCache` for environments that don't use bundlers.
})();
